import querystring, { ParsedUrlQueryInput } from 'querystring';
import _omit from 'lodash/omit';
import _get from 'lodash/get';
import _cloneDeep from 'lodash/cloneDeep';

import {
  MyChartOpenSchedulingConfig,
  PMCConfig,
  ProviderMatchProviderLocationV9,
  ProviderMatchProviderV9
} from '@kyruus/types';
import {
  DetailedProviderContent,
  DEFAULTS,
  FullAppointmentInfo,
  PurposeOptionsByRelationship
} from '@kyruus/provider-components';
import {
  VIMEO_VIDEO_BASE_URL,
  ABOUT_SECTION_EXCLUDE_SUMMARY_DATA_FIELDS,
  PROVIDER_NAME_HEADER_ID
} from './constants';
import { FROM_SEARCH_MAP } from '../../shared/provider-list';
import { VALID_PATIENT_RELS } from '../../utils/constants';
import {
  shouldRenderDirectBookInDrawer,
  MODULES,
  isModuleEnabled,
  isStaffPersona
} from 'Common/config';

import { BaseAppointmentInfo, BookingStrategies } from '../types';
import moment from 'moment';
import { AvailabilityProps } from '../components/ScheduleAppointment/types';
import { getBookingStrategy } from '../components/ScheduleAppointment/utilities';
import {
  getProviderIdForOpenScheduling,
  getVisitTypesFromProviderForOpenScheduling
} from '../components/OpenScheduling/utilities';
export { getProviderDisplayName } from '@kyruus/provider-components';

/**
 * Returns a function that waits ${timeout} to run when invoked and only runs if no other invocations happen
 * @param {function} `fn` function to debounce
 * @param {number} `timeout` ms to wait for invocations
 * @returns {function} debouncedFn
 */
export const debounce = (
  fn: (...args: any) => any,
  timeout = 200
): ((...args: any) => any) => {
  let timer: ReturnType<typeof setTimeout>;

  return (...args: any) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), timeout);
  };
};

/**
 * Returns a sort function that sorts alphabetically
 * @param {object} `{ order: 'asc' | 'desc' }` the sort order
 * @returns {function} (a: string, b: string) => number
 */
export const sortByAlpha =
  ({ order = 'asc' } = {}) =>
  (a: string, b: string): number =>
    order == 'asc' ? a.localeCompare(b) : b.localeCompare(a);

/**
 * Takes an array and inserts something between its items
 * @param {arr} `array` input array
 * @param {insert} thing to insert
 * @returns {array}
 */
export const insertBetweenItems = (arr: any[], insert: unknown): any[] =>
  arr.reduce((acc, item, index) => {
    const isLastItem = index === arr.length - 1;

    if (isLastItem) {
      return [...acc, item];
    }

    return [...acc, item, insert];
  }, []);

/**
 * Parses a YouTube oEmbed object to an embed url
 * @param {object} `data` the YouTube oEmbed json response data
 * @returns {string} embedUrl
 */
const parseYouTubeOembedDataToEmbedUrl = (
  data: {
    html: string;
  } & Record<string, string>
): string | null | undefined => {
  const { html } = data;
  const tempElem = document.createElement('template');

  tempElem.innerHTML = html;

  const iframe = tempElem.content.querySelector('iframe');
  const embedUrl = iframe?.getAttribute('src');

  tempElem.remove();

  return embedUrl;
};

/**
 * Converts a YouTube watch url to an embed url
 * @param {string} `url` the YouTube /watch?v=${video.id} url
 * @returns {string} `embedUrl`
 */
export const formatYouTubeVideoUrlToEmbed = async (
  url: string
): Promise<string> => {
  const isEmbedFormat = url.indexOf('embed') !== -1;

  if (isEmbedFormat) {
    return url;
  }

  const resp = await fetch(
    `https://youtube.com/oembed/?${new URLSearchParams({
      url,
      format: 'json'
    }).toString()}`,
    {
      method: 'GET',
      mode: 'cors'
    }
  );

  if (!resp.ok) {
    throw new Error('Error fetching youtube oembed data');
  }

  const data = await resp.json();
  const embedUrl = parseYouTubeOembedDataToEmbedUrl(data);

  if (!embedUrl) {
    throw new Error('Error parsing youtube oembed data');
  }

  return embedUrl;
};

/**
 * Function to determine if availability should render and type guard availability props
 * The main indicator of this should be the presence of apptInfo, but we can't render if we don't have
 * the tile creator function or the edit callback, and would want to stick with the button
 * @param props An object containing the possibly undefined props we're checking to render availability
 * @returns Boolean Type predicate confirming whether we should render availability/props are defined
 */
export const checkIfShouldRenderAvailability = (props: {
  apptInfo?: BaseAppointmentInfo;
  availabilityTileFactory?: (
    location: ProviderMatchProviderLocationV9
  ) => React.ReactElement;
  onEditApptInfo?: (apptInfo: FullAppointmentInfo) => void;
}): props is {
  apptInfo: BaseAppointmentInfo;
  availabilityTileFactory: (
    location: ProviderMatchProviderLocationV9
  ) => React.ReactElement;
  onEditApptInfo: (apptInfo: FullAppointmentInfo) => void;
} =>
  !!props.apptInfo && !!props.availabilityTileFactory && !!props.onEditApptInfo;

export interface GetAvailabilityPropsOptions {
  apptInfo?: FullAppointmentInfo;
  availabilityByLocationId?: {
    [key: string]: Array<any>;
  };
  availabilityTileFactory?: (
    location: ProviderMatchProviderLocationV9
  ) => React.ReactElement;
  onEditApptInfo?: (apptInfo: FullAppointmentInfo) => void;
  handleTimeSlotsClick: (
    date: string,
    time: string,
    provider_id: string,
    href: string
  ) => void;
  handleViewMoreClick: (_: any, href: string) => void;
}

export const getAvailabilityProps = (
  props: GetAvailabilityPropsOptions
): AvailabilityProps => {
  const enabled = Boolean(props.apptInfo) && Boolean(props.onEditApptInfo);

  return {
    enabled,
    apptInfo: props.apptInfo,
    availabilityByLocationId: props.availabilityByLocationId,
    availabilityTileFactory: props.availabilityTileFactory,
    onEditApptInfo: props.onEditApptInfo
  } as AvailabilityProps;
};

/**
 * Sorts an array of objects by the `.name` property in alphabetically ascending order (does not mutate array)
 * @param {array} `objects` the array to sort
 * @returns {array} `sortedObjects` a cloned array sorted by .name ascending
 */
export const sortByName = (objects: { name: string }[]): { name: string }[] => {
  const array = _cloneDeep(objects);

  return array.sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Takes in a provider field name and a customer config object and returns whether or not to show the field in the profile
 * @param {string} `fieldName` the field to check
 * @param {PMCConfig} `customerConfig` the customer config
 * @returns {boolean} whether or not to render the field in the profile
 */
export const isSummaryDataConfigured = (
  fieldName: string,
  customerConfig?: PMCConfig
): boolean => {
  // if no summary_data config exists
  if (
    !customerConfig?.provider_v9?.summary_data ||
    !Array.isArray(customerConfig.provider_v9.summary_data)
  ) {
    return false;
  }

  // if { field: fieldName } exists in summary_data config
  if (
    customerConfig.provider_v9.summary_data.find(
      ({ field }: { field?: string }) => field === fieldName
    )
  ) {
    return true;
  }

  // otherwise, return false
  return false;
};

/**
 * Takes in a soonest appointment UTC date
 * @param soonestAppointmentDate {string} The UTC date of the provider's soonest appointment
 * @returns {number} returns appointment available days within {x} days
 */
export const getAvailableWithinDays = (
  soonestAppointmentDate: string
): number => {
  const daysFromNowMs =
    new Date(soonestAppointmentDate).valueOf() - new Date().valueOf();
  const msPerDay = 12 * 2 * 60 * 60 * 1000;
  return Math.ceil(daysFromNowMs / msPerDay) || 0;
};

/**
 * Provider data getter
 * @param fieldConfig config.provider_v9.summary_data field config
 * @param provider ProviderMatchProviderV9 object
 * @returns {string | boolean | null} provider data if it exists, null otherwise
 */
export const extractProviderDataContent = (
  fieldConfig: {
    field: keyof ProviderMatchProviderV9;
    subfield?: string;
    ignore?: Array<string>;
  },
  provider: Record<string, unknown>
): string | boolean | null => {
  const { field, subfield, ignore } = fieldConfig;
  const data = _get(provider, field, null);

  if (typeof data === 'boolean') {
    if (Array.isArray(ignore) && ignore.includes(data.toString())) {
      return null;
    }

    return data;
  }

  if ((typeof data === 'string' || typeof data === 'number') && Boolean(data)) {
    if (Array.isArray(ignore) && ignore.includes(data.toString())) {
      return null;
    }

    return String(data);
  }

  if (Array.isArray(data)) {
    // assume all items are strings if first item is a string
    if (typeof data[0] === 'string' || typeof data[0] === 'number') {
      if (Array.isArray(ignore)) {
        return data.filter((d) => !ignore.includes(String(d))).join('\n');
      }

      return data.join('\n');
    }

    // otherwise assume items are objects
    if (typeof data[0] === 'object' && subfield) {
      return data
        .reduce((acc, item) => {
          const fieldData = item[subfield];

          if (Array.isArray(ignore) && ignore.includes(String(fieldData))) {
            return acc;
          }

          return [...acc, fieldData];
        }, [])
        .join('\n');
    }

    return null;
  }

  return null;
};

/**
 * Checks to see if the `debug_provider_profile` cookie is set to true
 * @returns {boolean} whether or not the cookie exists and is set to true
 */
export const isDebugMode = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const hasDebugCookie =
    document &&
    document.cookie &&
    document.cookie.includes('debug_provider_profile');

  if (!hasDebugCookie) {
    return false;
  }

  return document.cookie.includes('debug_provider_profile=true');
};

/**
 * Checks to see if a given url is a YouTube url
 * @returns {boolean}
 */
export const isUrlYouTube = (url: string) =>
  url.indexOf('youtube') !== -1 || url.indexOf('youtu.be') !== -1;

/**
 * Checks to see if a piece of config.provider_v9.summary_data exists on a provider
 * @param fieldConfig config.provider_v9.summary_data field config
 * @param provider ProviderMatchProviderV9 object
 * @returns {boolean} whether or not the data exists on the provider
 */
export const summaryDataConfigHasProviderData = (
  fieldConfig: {
    field: keyof ProviderMatchProviderV9;
    subfield?: string;
    ignore?: Array<string>;
  },
  provider: Record<string, unknown>
) => {
  if (extractProviderDataContent(fieldConfig, provider) !== null) {
    return true;
  }

  return false;
};

/**
 * Returns the video url for a provider.
 * Corrects invalid vimeo urls and returns all others without a change
 *
 * @param url
 * @returns url
 */
export const getVideoUrl = (url: string): string => {
  if (url.includes('vimeo')) {
    const match = url.match(/vimeo.com(.*)?\/(\d+)/);
    if (match && match[2]) {
      const videoId = match[2];
      return `${VIMEO_VIDEO_BASE_URL}/${videoId}`;
    }
  }
  return url;
};

/**
 * Return a normalized star count based on a review rating and
 * a guess of the rating scale. Each rating data object does not include the original scale.
 *
 * e.g If the rating is 6, this will guess that the rating scale is 1-10 then convert
 * the star count to 60% of PROVIDER_RATING_SCALE
 *
 * @param {number} rating rating value
 * @returns {number}
 */
export const getReviewStarCount = (rating = 1): number => {
  const starRating = !rating ? 1 : rating;

  const ratingScale = DEFAULTS.PROVIDER_RATING_SCALE;
  const scaleEnd = Math.ceil(starRating / ratingScale) * ratingScale;
  const percentOfScale = starRating / scaleEnd;
  return Math.max(
    0,
    Math.min(ratingScale, Math.round(ratingScale * percentOfScale))
  );
};

/**
 * Returns a formatted date string for a review
 * @param {string} date a review date string
 * @returns {string} formatted date string m/d/yyyy
 */
export const getReviewDateString = (date: string): string =>
  moment(date).format('M/D/YYYY');

/**
 * Prunes ignored values off the provider.network_affiliations list
 * @param {object} provider
 * @param {object} config
 * @returns {object} provider object with network_affiliations field pruned
 */
export const pruneIgnoredNetworkAffiliations = (
  provider: ProviderMatchProviderV9,
  config: PMCConfig
) => {
  const networkAffiliations = provider.network_affiliations || [];
  const summaryDataConfig = config.provider_v9?.summary_data;
  const networkAffiliationsSummaryDataConfig = (summaryDataConfig || []).find(
    (c: any) => c.field === 'network_affiliations'
  );
  const hasIgnoredValues =
    Array.isArray(networkAffiliationsSummaryDataConfig?.ignore) &&
    networkAffiliationsSummaryDataConfig.ignore.length > 0;

  if (hasIgnoredValues) {
    const prunedNetworkAffiliations = networkAffiliations.filter(
      (n) =>
        !(networkAffiliationsSummaryDataConfig?.ignore || []).includes(n.name)
    );

    return { ...provider, network_affiliations: prunedNetworkAffiliations };
  }

  return provider;
};

/**
 * Prunes ignored values off a field on a provider object
 * only works for array fields
 * @param {object} provider
 * @param {string} fieldKey
 * @param {object} config
 * @returns {object} provider object with fieldKey field pruned
 */
export const pruneIgnoredValues = (
  provider: ProviderMatchProviderV9,
  fieldKey: string,
  config: PMCConfig
) => {
  const fieldValues =
    (provider[fieldKey as keyof ProviderMatchProviderV9] as Array<object>) ||
    [];
  const summaryDataConfig = config.provider_v9?.summary_data;
  const fieldSummaryDataConfig = (summaryDataConfig || []).find(
    (c: any) => c.field === fieldKey
  );
  const hasIgnoredValues =
    Array.isArray(fieldSummaryDataConfig?.ignore) &&
    fieldSummaryDataConfig.ignore.length > 0;

  if (hasIgnoredValues) {
    const prunedFieldValues = fieldValues.filter(
      (n) => !(fieldSummaryDataConfig?.ignore || []).includes(n)
    );
    return { ...provider, [fieldKey]: prunedFieldValues };
  }
  return provider;
};

/**
 * Returns a list of a provider's specialty and subspecialty names
 * @param {ProviderMatchProviderV9} provider a v9 provider object
 * @returns a list of the provider's specialties and subspecialty names
 */
export const getSpecialtyItems = (provider: ProviderMatchProviderV9) => {
  const specialtiesItems = (provider.specialties || []).reduce((acc, curr) => {
    const specialtyName = curr.name;

    const subspecialtyNames =
      curr.subspecialties && curr.subspecialties.length > 0
        ? curr.subspecialties.map((s) => s.name)
        : [];

    acc.push(specialtyName);
    subspecialtyNames.forEach((s) => acc.push(s));

    return acc;
  }, [] as string[]);

  return specialtiesItems;
};

/**
 * Retrieves the phone contact value for a given provider.
 * @param provider - The provider object.
 * @returns The phone contact value if found, otherwise undefined.
 */
export const getProviderPhone = (provider: ProviderMatchProviderV9) => {
  return provider?.contacts?.find(
    ({ contact_type }) => contact_type === 'phone'
  )?.value;
};

/**
 * Extracts and deduplicates book_as_type_id values from provider's appointment purposes.
 * Filters by source_system to only include IDs matching the specified source system.
 * Returns an empty array if extraction fails or no data is available.
 * @param provider - The provider object.
 * @param sourceSystem - The source system to filter by (e.g., location's source_system).
 * @returns Array of unique book_as_type_id strings.
 */
export const extractBookAsTypeIds = (
  provider: ProviderMatchProviderV9,
  sourceSystem: string
): string[] => {
  try {
    if (!provider?.appointment_ehr_purposes) {
      return [];
    }

    const bookAsTypeIds = provider.appointment_ehr_purposes.reduce<string[]>(
      (acc, purpose) => {
        const ids = (purpose.ehr_data ?? [])
          .filter((ehrData) => ehrData.source_system === sourceSystem)
          .map((ehrData) => ehrData.book_as_type_id)
          .filter((id): id is string => Boolean(id));
        return [...acc, ...ids];
      },
      []
    );

    return Array.from(new Set(bookAsTypeIds));
  } catch (error) {
    return [];
  }
};

export const getAboutSectionSummaryData = (
  config: PMCConfig,
  provider: ProviderMatchProviderV9
) => {
  return (config.provider_v9?.summary_data || []).filter(
    (fieldConfig) =>
      !ABOUT_SECTION_EXCLUDE_SUMMARY_DATA_FIELDS.includes(fieldConfig.field) &&
      summaryDataConfigHasProviderData(
        fieldConfig,
        provider as unknown as Record<string, unknown>
      )
  );
};

export const showAboutSection = (
  config: PMCConfig,
  provider: ProviderMatchProviderV9
) => {
  return getAboutSectionSummaryData(config, provider).length > 0;
};

export function showAppointmentsSection(
  config: PMCConfig,
  provider?: ProviderMatchProviderV9
): boolean {
  if (!provider) {
    return false;
  }

  const bookingStrategy = getBookingStrategy(config, provider);

  switch (bookingStrategy) {
    case BookingStrategies.OpenSchedulingWidget:
      return true;
    case BookingStrategies.Availability:
    case BookingStrategies.Button:
    default:
      return false;
  }
}

export function isProviderCompatibleWithOpenScheduling(
  openSchedulingConfig: MyChartOpenSchedulingConfig,
  provider: ProviderMatchProviderV9
) {
  const providerId = getProviderIdForOpenScheduling(provider);

  if (!providerId) {
    return false;
  }

  const visitTypes = getVisitTypesFromProviderForOpenScheduling(
    openSchedulingConfig,
    provider
  );

  if (visitTypes.length === 0) {
    return false;
  }

  return true;
}

export const showClinicalInterestsSection = (
  provider: ProviderMatchProviderV9
) => {
  const { clinical_interests } = provider;

  return Boolean(clinical_interests) && typeof clinical_interests === 'string';
};

export const showExpertiseSection = (provider: ProviderMatchProviderV9) => {
  const { training, board_certifications, clinical_trials } = provider;

  const hasTraining = Array.isArray(training) && training.length > 0;
  const hasBoardCertifications =
    Array.isArray(board_certifications) && board_certifications.length > 0;
  const hasClinicalTrials =
    Array.isArray(clinical_trials) && clinical_trials.length > 0;

  return hasTraining || hasBoardCertifications || hasClinicalTrials;
};

export const showInsuranceSection = (
  config: PMCConfig,
  provider: ProviderMatchProviderV9
) => {
  const prunedInsuranceAccepted = pruneIgnoredValues(
    provider,
    'insurance_accepted',
    config
  ).insurance_accepted;
  return prunedInsuranceAccepted.length > 0;
};

export const showReviewSection = (provider: ProviderMatchProviderV9) => {
  const { reviews } = provider;
  const aggregateRatings = reviews?.aggregate_ratings;
  if (!aggregateRatings) {
    return false;
  }
  const reviewCount = aggregateRatings.review_count;
  const ratingCount = aggregateRatings.rating_count;

  const hasDataForRatingsSection =
    typeof reviewCount === 'number' && typeof ratingCount === 'number';
  const hasDataForReviewsSection = Boolean(reviews?.reviews?.length);
  return hasDataForRatingsSection || hasDataForReviewsSection;
};

export const showCareTeamSection = (
  config: PMCConfig,
  careTeamProviders: DetailedProviderContent[]
) => {
  return (
    config.feature_flags?.provider_profile_care_team === true &&
    careTeamProviders?.length > 0
  );
};

export const showAltProvidersSection = (
  config: PMCConfig,
  altProviders: DetailedProviderContent[]
) => {
  return (
    config?.show_provider_profile_alt_providers === true &&
    altProviders?.length > 0
  );
};

export const getTabControlId = (tabId: string) => {
  return `profile-nav-${tabId}`;
};

export const getNavItemAriaLabelledBy = (navItemId: string) => {
  const tabControlId = getTabControlId(navItemId);
  return `${PROVIDER_NAME_HEADER_ID} ${tabControlId}`;
};

/** list of query params to omit when building the "back to" links */
const OMIT_QUERY_PARAMS = ['from'];

/**
 * Function that returns the BackToSearchLink's destination from the currentQuery
 * @param {object} currentQuery react-router history's search query
 * @returns {string | {pathname: string, search: string}}}
 */
export const getBackToSearchLinkDestination = ({
  currentQuery
}: {
  currentQuery: object;
}) => {
  let linkDestination;
  const { specialty_page, location, specialty, from } =
    currentQuery as ParsedUrlQueryInput;

  if (specialty_page) {
    const nearParam = location
      ? `/near/${encodeURIComponent(String(location))}`
      : '';

    linkDestination = `/specialty/${encodeURIComponent(String(specialty))}${nearParam}`;
  } else {
    /** '/search' is the default pathname whether or not we are coming `from` FROM_SEARCH_LIST*/
    let pathname = '/search';

    if (from === FROM_SEARCH_MAP) {
      pathname = '/locations';
    }

    linkDestination = {
      pathname,
      search: querystring.stringify(
        _omit(currentQuery as ParsedUrlQueryInput, OMIT_QUERY_PARAMS)
      )
    };
  }

  return linkDestination;
};

export const getNewSearchLinkDestination = ({
  config,
  currentQuery: _currentQuery
}: {
  config: PMCConfig;
  currentQuery?: ParsedUrlQueryInput;
}) => {
  const newSearchLink = config?.search_home || '/';
  return newSearchLink;
};

export const canSkipModal = (
  provider: ProviderMatchProviderV9,
  config: PMCConfig
) => {
  if (isStaffPersona(config)) {
    return true;
  }
  const {
    modal_display: { skip_modal: skipModal, virtual_care: isVirtualCare } = {}
  } = config;
  const virtualCareIsEnabled = Boolean(
    isVirtualCare && provider.virtual_care_url
  );
  // if skipModal is false in config and DB is not rendered in drawer, always return false
  if (!skipModal) {
    return false;
  }

  // if both virtual care is enabled and the provider has a book online url, do not skip the modal
  if (virtualCareIsEnabled && provider.book_online_url) {
    return false;
  }

  // only skip it if they have either one
  return provider.book_online_url || virtualCareIsEnabled;
};

/**
 * Determines if the appointment info is valid
 * @param {Partial<AppointmentInfo>} apptInfo
 * @returns {boolean}
 */
export const isApptInfoValid = (
  apptInfo: Partial<FullAppointmentInfo> | null | undefined
): apptInfo is Partial<FullAppointmentInfo> => {
  if (!apptInfo) {
    return false;
  }

  if (!apptInfo.relationship) {
    return false;
  }

  if (!VALID_PATIENT_RELS.includes(apptInfo.relationship)) {
    return false;
  }

  return true;
};

/**
 * Determines if the appointment info is valid and complete
 * @param {AppointmentInfo} apptInfo
 * @returns
 */
export const isApptInfoValidAndComplete = (
  apptInfo: Partial<FullAppointmentInfo> | null | undefined
) => {
  if (!isApptInfoValid(apptInfo)) {
    return false;
  }

  if (!apptInfo.purpose) {
    return false;
  }

  if (typeof apptInfo.purpose !== 'string') {
    return false;
  }

  return true;
};

/**
 * Determines if the booking button should be rendered as an anchor tag.
 * It should return false if the customer is configured to use the CTA modal
 * or to render DirectBook in a drawer
 * @param {object} provider provider object
 * @param {object} config customer config
 * @returns {boolean}
 */
export const shouldRenderBookingBtnAsLink = (
  provider: ProviderMatchProviderV9,
  config: PMCConfig
) => {
  if (
    canSkipModal(provider, config) &&
    !shouldRenderDirectBookInDrawer(config)
  ) {
    return true;
  }
  return false;
};

/**
 * Determine if the default appointment info should be applied.
 * @param {object} config
 * @param {object} providerContext { apptInfo, purposes, profile }
 * @returns {boolean}
 */
export const shouldApplyDefaultAppointmentInfo = (
  config: PMCConfig,
  {
    apptInfo,
    purposes,
    profile
  }: {
    apptInfo: Partial<FullAppointmentInfo>;
    purposes: Partial<PurposeOptionsByRelationship>;
    profile: { provider: Partial<ProviderMatchProviderV9> };
  }
) => {
  if (
    !config.display_availability_in_search ||
    (config.feature_flags &&
      !(config.feature_flags as Record<string, boolean | undefined>)
        .use_provider_profile_heads_up_availability)
  ) {
    return false;
  }
  const directBookCapable = _get(
    profile,
    'provider.direct_book_capable',
    false
  );
  const directBookUrlExists = Boolean(
    _get(profile, 'provider.book_online_url')
  );
  const directBookEnabled = directBookCapable && directBookUrlExists;
  const apptInfoIsEmpty =
    apptInfo.purpose == null && apptInfo.relationship == null;
  const purposesExist =
    purposes &&
    (Boolean(purposes.new && purposes.new.length) ||
      Boolean(purposes.established && purposes.established.length));
  const agentModeEnabled = isModuleEnabled(config, MODULES.VISIBILITY_AGENT);

  return (
    agentModeEnabled === false &&
    directBookEnabled &&
    apptInfoIsEmpty &&
    purposesExist
  );
};

/**
 * Determines if the provider has purposes
 *
 * @param {object} purposes
 * @returns {boolean}
 */
export const purposesExist = (
  purposes: Partial<PurposeOptionsByRelationship> | null | undefined
) => {
  if (!purposes || !purposes.new || !purposes.established) return false;

  return purposes.new.length > 0 || purposes.established.length > 0;
};
